import React from "react";
import { IonButton, IonCard, IonIcon } from "@ionic/react";
import { addCircle, copyOutline } from "ionicons/icons";

/**
 * Reusable Add / Copy controls for the New Entry grid.
 * Compact tiles sit in the collapsed multi-column grid; the full-width row is used
 * for a single column, an expanded entry, or when add actions wrap onto their own row.
 */

/** Desktop: icon + label inline. Phone: stacked icon above label. */
const AddActionButtonContent: React.FC<{ icon: string; label: string }> = ({ icon, label }) => (
    <>
        <div className="d-none d-md-flex align-items-center ion-text-wrap">
            <IonIcon icon={icon} size="medium" className="pr-2 text-white" />
            {label}
        </div>
        <div className="d-flex d-md-none flex-column align-items-center justify-content-center ion-text-center">
            <IonIcon icon={icon} className="entry-add-action-icon text-white" />
            <span className="entry-add-action-label font-weight-bold">{label}</span>
        </div>
    </>
);

/** Same height as a minified entry card so Add/Copy tiles share a row with horse cards. */
export const CompactAddActionCard: React.FC<{
    color: "success" | "primary";
    icon: string;
    label: string;
    disabled?: boolean;
    onClick: () => void;
}> = ({ color, icon, label, disabled = false, onClick }) => (
    <IonCard
        mode="md"
        color={color}
        className={`rounded-3 mx-1 m-0 entry-add-action-tile ${disabled ? "entry-add-action-tile--disabled" : "entry-add-action-tile--enabled"}`}
        onClick={() => {
            if (disabled) return;
            onClick();
        }}>
        <div className="d-flex flex-column align-items-center justify-content-center ion-text-center px-2 entry-add-action-tile__content">
            <IonIcon icon={icon} className="entry-add-action-tile__icon" />
            <span className="font-weight-bold display-4 text-white entry-add-action-tile__label">{label}</span>
        </div>
    </IonCard>
);

/** Full-row leftover action: inline icon + label at the paired-button height, not minified-card height. */
export const FullRowAddActionButton: React.FC<{
    color: "success" | "primary";
    icon: string;
    label: string;
    disabled?: boolean;
    onClick: () => void;
}> = ({ color, icon, label, disabled = false, onClick }) => (
    <IonButton
        expand="block"
        className="entry-add-action-button entry-add-action-button--full-row font-weight-bold m-0 mx-1 display-4 shadow-lg--hover"
        color={color}
        disabled={disabled}
        onClick={() => {
            if (disabled) return;
            onClick();
        }}>
        <div className="d-flex align-items-center justify-content-center ion-text-wrap">
            <IonIcon icon={icon} size="medium" className="pr-2 text-white" />
            {label}
        </div>
    </IonButton>
);

interface FullWidthAddEntryActionsProps {
    addActionsDisabled: boolean;
    canCopyPreviousEntry: boolean;
    onAddNewEntry: () => void;
    onCopyPreviousEntry: () => void;
}

/** Side-by-side Add + Copy buttons used when the compact in-grid tiles are not shown. */
export const FullWidthAddEntryActions: React.FC<FullWidthAddEntryActionsProps> = ({
    addActionsDisabled,
    canCopyPreviousEntry,
    onAddNewEntry,
    onCopyPreviousEntry,
}) => (
    <div className="entry-add-actions-row">
        <div className="entry-add-action-col">
            <IonButton
                expand="block"
                className="entry-add-action-button font-weight-bold m-0 display-4 shadow-lg--hover"
                color="success"
                onClick={() => {
                    if (addActionsDisabled) return;
                    onAddNewEntry();
                }}>
                <AddActionButtonContent icon={addCircle} label="Add New Entry" />
            </IonButton>
        </div>
        <div className="entry-add-action-col">
            <IonButton
                expand="block"
                className="entry-add-action-button font-weight-bold m-0 display-4 shadow-lg--hover"
                color="primary"
                // Keep enabled while another section is expanded so click still no-ops via the page handler;
                // only grey out when there is genuinely nothing to copy.
                disabled={!canCopyPreviousEntry && !addActionsDisabled}
                onClick={onCopyPreviousEntry}>
                <AddActionButtonContent icon={copyOutline} label="Copy Previous Entry" />
            </IonButton>
        </div>
    </div>
);
